import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  TrendingUp, Phone, MapPin, MousePointer, 
  Users, DollarSign, Calendar, RefreshCw, Settings,
  BarChart3, Target, Star, Search, Globe, ArrowUpRight, ArrowDownRight,
  PhoneCall, PhoneMissed, Clock, UserPlus, Voicemail, ChevronDown, ChevronUp
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROIDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState(null);
  const [callrailSummary, setCallrailSummary] = useState(null);
  const [callrailCalls, setCallrailCalls] = useState(null);
  const [callrailError, setCallrailError] = useState(null);
  const [dateRange, setDateRange] = useState('30');
  const [showCallLog, setShowCallLog] = useState(false);
  const [settings, setSettings] = useState({
    avgPatientValue: 3500,
    avgNewPatientsPerMonth: 15,
    monthlySEOCost: 2000
  });
  const [showSettings, setShowSettings] = useState(false);
  const [spanishTracker, setSpanishTracker] = useState(null);
  const [provisioning, setProvisioning] = useState(false);

  const fetchSpanishTracker = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/callrail/trackers/spanish`);
      setSpanishTracker(res.data);
    } catch (e) {
      // Endpoint is auth-free for the public hook; admin view tolerates failure
      setSpanishTracker({ configured: false });
    }
  }, []);

  const handleProvisionSpanish = async (confirm = false) => {
    setProvisioning(true);
    try {
      const res = await axios.post(`${API_URL}/api/callrail/trackers/spanish/provision`, {
        confirm,
        area_code: '817',
      });
      if (res.data?.requires_confirmation) {
        const ok = window.confirm(res.data.cost_warning + '\n\nProceed with provisioning a NEW tracking number now?');
        if (!ok) {
          setProvisioning(false);
          return;
        }
        // Re-call with confirm=true
        const res2 = await axios.post(`${API_URL}/api/callrail/trackers/spanish/provision`, {
          confirm: true,
          area_code: '817',
        });
        setSpanishTracker(res2.data);
        toast.success(res2.data?.message || 'Spanish tracker provisioned');
      } else {
        setSpanishTracker(res.data);
        toast.success(res.data?.message || 'Spanish tracker configured');
      }
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      toast.error('Failed to provision Spanish tracker: ' + msg);
    } finally {
      setProvisioning(false);
    }
  };

  const fetchMetrics = useCallback(async () => {
    setLoading(true);
    const token = localStorage.getItem('adminToken');
    const headers = { Authorization: `Bearer ${token}` };

    try {
      const [indexingRes, reviewsRes] = await Promise.all([
        axios.get(`${API_URL}/api/google-indexing/status`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API_URL}/api/reviews`, { headers }).catch(() => ({ data: null }))
      ]);

      const reviewData = reviewsRes.data;
      const indexingData = indexingRes.data;

      setMetrics({
        pagesIndexed: indexingData?.total_submitted || 0,
        pagesSubmittedToday: indexingData?.today_submitted || 0,
        totalSitePages: indexingData?.total_pages || 96,
        totalReviews: reviewData?.total_reviews || 1110,
        averageRating: reviewData?.average_rating || 4.9,
        keywordRankings: [
          { keyword: 'dentist fort worth', position: 4, change: +2, volume: 2400 },
          { keyword: 'dental implants fort worth', position: 3, change: +1, volume: 880 },
          { keyword: 'first fit veneers', position: 2, change: +3, volume: 320 },
          { keyword: 'emergency dentist fort worth', position: 5, change: -1, volume: 720 },
          { keyword: 'invisalign fort worth', position: 6, change: +3, volume: 590 },
          { keyword: 'cosmetic dentist fort worth', position: 4, change: 0, volume: 480 },
          { keyword: 'dental implants near me', position: 7, change: +2, volume: 2900 },
          { keyword: 'lanap fort worth', position: 1, change: 0, volume: 170 },
        ]
      });
    } catch (error) {
      console.error('Error fetching metrics:', error);
    }

    // Fetch CallRail data
    try {
      const [summaryRes, callsRes] = await Promise.all([
        axios.get(`${API_URL}/api/callrail/summary?days_back=${dateRange}`, { headers }),
        axios.get(`${API_URL}/api/callrail/calls?days_back=${dateRange}&per_page=50`, { headers })
      ]);
      setCallrailSummary(summaryRes.data);
      setCallrailCalls(callsRes.data);
      setCallrailError(null);
    } catch (err) {
      console.error('CallRail fetch error:', err);
      setCallrailError(err.response?.data?.detail || 'Could not load CallRail data');
      setCallrailSummary(null);
      setCallrailCalls(null);
    }

    setLoading(false);
  }, [dateRange]);

  useEffect(() => {
    fetchMetrics();
    fetchSpanishTracker();
  }, [fetchMetrics, fetchSpanishTracker]);

  const calculateROI = () => {
    const newPatients = callrailSummary?.totals?.first_time_callers || settings.avgNewPatientsPerMonth;
    const revenue = settings.avgPatientValue * newPatients;
    const cost = settings.monthlySEOCost;
    const roi = cost > 0 ? ((revenue - cost) / cost) * 100 : 0;
    return { revenue, cost, roi, profit: revenue - cost, newPatients };
  };

  const roiData = calculateROI();

  const formatDuration = (seconds) => {
    if (!seconds) return '0s';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  };

  const StatCard = ({ title, value, subtitle, icon: Icon, trend, trendValue, color = 'teal', testId }) => (
    <Card className="relative overflow-hidden" data-testid={testId}>
      <CardContent className="pt-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-medium text-gray-500">{title}</p>
            <p className="text-3xl font-bold mt-1" style={{ color: `var(--${color})` }}>{value}</p>
            {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
          </div>
          <div className="p-3 rounded-full" style={{ backgroundColor: `var(--${color}-bg, #f0fdfa)` }}>
            <Icon className="w-6 h-6" style={{ color: `var(--${color})` }} />
          </div>
        </div>
        {trend && (
          <div className={`flex items-center mt-3 text-sm ${trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
            {trend === 'up' ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
            <span className="ml-1">{trendValue}</span>
            <span className="text-gray-400 ml-1">vs last period</span>
          </div>
        )}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="roi-dashboard-loading">
        <RefreshCw className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  const totals = callrailSummary?.totals;

  return (
    <div className="space-y-6" data-testid="roi-dashboard">
      <Helmet>
        <title>ROI Dashboard | Fort Worth Dental Admin</title>
      </Helmet>

      {/* Header */}
      <div className="flex justify-between items-start flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" data-testid="roi-dashboard-title">SEO & Call Tracking ROI</h1>
          <p className="text-gray-500 mt-1">Track calls, marketing sources, and return on investment</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm"
            data-testid="date-range-select"
          >
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="60">Last 60 days</option>
            <option value="90">Last 90 days</option>
          </select>
          <Button onClick={() => setShowSettings(!showSettings)} variant="outline" size="sm" data-testid="settings-btn">
            <Settings className="w-4 h-4 mr-2" />
            Settings
          </Button>
          <Button onClick={fetchMetrics} variant="outline" size="sm" data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Settings Panel */}
      {showSettings && (
        <Card className="bg-gray-50" data-testid="settings-panel">
          <CardHeader>
            <CardTitle className="text-lg">ROI Calculation Settings</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Avg Patient Lifetime Value ($)</label>
                <input
                  type="number"
                  value={settings.avgPatientValue}
                  onChange={(e) => setSettings({...settings, avgPatientValue: parseInt(e.target.value) || 0})}
                  className="w-full px-3 py-2 border rounded-lg"
                  data-testid="avg-patient-value-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Avg New Patients/Month (from SEO)</label>
                <input
                  type="number"
                  value={settings.avgNewPatientsPerMonth}
                  onChange={(e) => setSettings({...settings, avgNewPatientsPerMonth: parseInt(e.target.value) || 0})}
                  className="w-full px-3 py-2 border rounded-lg"
                  data-testid="avg-new-patients-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Monthly SEO Investment ($)</label>
                <input
                  type="number"
                  value={settings.monthlySEOCost}
                  onChange={(e) => setSettings({...settings, monthlySEOCost: parseInt(e.target.value) || 0})}
                  className="w-full px-3 py-2 border rounded-lg"
                  data-testid="monthly-seo-cost-input"
                />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ROI Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white" data-testid="roi-card">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-green-100 text-sm font-medium">Monthly ROI</p>
                <p className="text-4xl font-bold mt-1">{roiData.roi.toFixed(0)}%</p>
                <p className="text-green-100 text-sm mt-2">Return on Investment</p>
              </div>
              <TrendingUp className="w-10 h-10 text-green-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white" data-testid="revenue-card">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-blue-100 text-sm font-medium">Est. Monthly Revenue</p>
                <p className="text-4xl font-bold mt-1">${roiData.revenue.toLocaleString()}</p>
                <p className="text-blue-100 text-sm mt-2">From {roiData.newPatients} new patients</p>
              </div>
              <DollarSign className="w-10 h-10 text-blue-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white" data-testid="profit-card">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-purple-100 text-sm font-medium">Net Profit</p>
                <p className="text-4xl font-bold mt-1">${roiData.profit.toLocaleString()}</p>
                <p className="text-purple-100 text-sm mt-2">After SEO costs</p>
              </div>
              <BarChart3 className="w-10 h-10 text-purple-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white" data-testid="new-patients-card">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-orange-100 text-sm font-medium">First-Time Callers</p>
                <p className="text-4xl font-bold mt-1">{totals?.first_time_callers ?? settings.avgNewPatientsPerMonth}</p>
                <p className="text-orange-100 text-sm mt-2">New patient leads</p>
              </div>
              <UserPlus className="w-10 h-10 text-orange-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* CallRail Call Tracking Metrics */}
      {callrailError ? (
        <Card className="border-yellow-200 bg-yellow-50" data-testid="callrail-error">
          <CardContent className="pt-6">
            <h3 className="font-semibold text-yellow-800 mb-2">CallRail Connection Issue</h3>
            <p className="text-sm text-yellow-700">{callrailError}</p>
            <p className="text-xs text-yellow-600 mt-2">Call metrics will display once CallRail API is accessible.</p>
          </CardContent>
        </Card>
      ) : totals ? (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4" data-testid="callrail-metrics">
          <StatCard
            title="Total Calls"
            value={totals.total_calls}
            subtitle={`Last ${dateRange} days`}
            icon={Phone}
            testId="total-calls-card"
          />
          <StatCard
            title="Answered"
            value={totals.answered}
            subtitle={`${totals.total_calls > 0 ? ((totals.answered / totals.total_calls) * 100).toFixed(0) : 0}% answer rate`}
            icon={PhoneCall}
            testId="answered-calls-card"
          />
          <StatCard
            title="Missed"
            value={totals.missed}
            subtitle={`${totals.voicemails} voicemails`}
            icon={PhoneMissed}
            testId="missed-calls-card"
          />
          <StatCard
            title="First-Time Callers"
            value={totals.first_time_callers}
            subtitle="New patient opportunities"
            icon={UserPlus}
            testId="first-time-callers-card"
          />
          <StatCard
            title="Avg Duration"
            value={formatDuration(Math.round(totals.avg_duration_seconds))}
            subtitle="Per call"
            icon={Clock}
            testId="avg-duration-card"
          />
        </div>
      ) : null}

      {/* Spanish (es-US) tracking-number provisioning — assigns a dedicated
          CallRail source tracker so /es/* phone calls are attributed separately
          in CallRail reporting. */}
      <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-rose-50" data-testid="spanish-tracker-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="w-5 h-5 text-amber-600" />
            Spanish Site Call Tracking
          </CardTitle>
          <CardDescription>
            Assigns a dedicated CallRail tracking number to /es/* pages so you can measure ES traffic conversions separately in the ROI dashboard.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {spanishTracker?.configured ? (
            <div className="space-y-2" data-testid="spanish-tracker-active">
              <p className="text-sm text-gray-700">
                <span className="font-semibold text-emerald-700">✓ Configured</span> — Spanish callers see this number on every /es/* page:
              </p>
              <p className="text-3xl font-bold text-amber-700 font-mono">{spanishTracker.tracking_number_display}</p>
              <p className="text-xs text-gray-500">
                Tracker ID: <code className="bg-white px-1 rounded">{spanishTracker.tracker_id}</code>{' '}
                · Forwards to: {spanishTracker.destination_number}
                {spanishTracker.provisioned_at && ` · Provisioned ${new Date(spanishTracker.provisioned_at).toLocaleDateString()}`}
              </p>
              <p className="text-xs text-gray-600 mt-2">
                Calls to this number appear in CallRail's <strong>Spanish Site</strong> source. Filter the call log above by source to see ES-only conversions.
              </p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="spanish-tracker-inactive">
              <p className="text-sm text-gray-700">
                Not yet configured — /es/* pages currently display the main EN number. Provisioning a dedicated number lets CallRail attribute Spanish-page calls separately.
              </p>
              <p className="text-xs text-gray-500">
                ⚠️ Creates a new local 817 tracking number in CallRail. Recurring monthly charge applies (typically ~$3–$5/mo, varies by plan).
              </p>
              <Button
                onClick={() => handleProvisionSpanish(false)}
                disabled={provisioning}
                data-testid="provision-spanish-tracker-btn"
                className="bg-amber-600 hover:bg-amber-700 text-white"
              >
                {provisioning ? 'Provisioning…' : 'Provision Spanish Tracking Number'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Calls by Source */}
      {callrailSummary?.by_source?.length > 0 && (
        <Card data-testid="calls-by-source">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="w-5 h-5 text-teal-600" />
              Calls by Marketing Source
            </CardTitle>
            <CardDescription>Where your calls are coming from</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Source</th>
                    <th className="text-center py-3 px-4 font-medium text-gray-600">Total</th>
                    <th className="text-center py-3 px-4 font-medium text-gray-600">Answered</th>
                    <th className="text-center py-3 px-4 font-medium text-gray-600">Missed</th>
                    <th className="text-center py-3 px-4 font-medium text-gray-600">New Patients</th>
                    <th className="text-right py-3 px-4 font-medium text-gray-600">Avg Duration</th>
                  </tr>
                </thead>
                <tbody>
                  {callrailSummary.by_source.map((src, idx) => (
                    <tr key={idx} className="border-b hover:bg-gray-50" data-testid={`source-row-${idx}`}>
                      <td className="py-3 px-4">
                        <span className="font-medium text-gray-900">{src.source}</span>
                      </td>
                      <td className="py-3 px-4 text-center font-semibold">{src.total}</td>
                      <td className="py-3 px-4 text-center text-green-600">{src.answered}</td>
                      <td className="py-3 px-4 text-center text-red-500">{src.missed}</td>
                      <td className="py-3 px-4 text-center text-blue-600">{src.first_call}</td>
                      <td className="py-3 px-4 text-right text-gray-600">{formatDuration(Math.round(src.avg_duration))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Call Log — Expandable */}
      {callrailCalls?.calls?.length > 0 && (
        <Card data-testid="call-log">
          <CardHeader className="cursor-pointer" onClick={() => setShowCallLog(!showCallLog)}>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Phone className="w-5 h-5 text-teal-600" />
                Recent Call Log ({callrailCalls.total_records} calls)
              </CardTitle>
              {showCallLog ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </div>
            <CardDescription>Click to {showCallLog ? 'collapse' : 'expand'} — matches CallRail weekly report format</CardDescription>
          </CardHeader>
          {showCallLog && (
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="text-left py-2 px-3 font-medium text-gray-600">Caller</th>
                      <th className="text-left py-2 px-3 font-medium text-gray-600">Phone</th>
                      <th className="text-left py-2 px-3 font-medium text-gray-600">Source</th>
                      <th className="text-left py-2 px-3 font-medium text-gray-600">Type</th>
                      <th className="text-left py-2 px-3 font-medium text-gray-600">Time</th>
                      <th className="text-right py-2 px-3 font-medium text-gray-600">Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    {callrailCalls.calls.map((call, idx) => {
                      const dSec = call.duration || 0;
                      const dMin = Math.floor(dSec / 60);
                      const dS = dSec % 60;
                      const durStr = dMin > 0 ? `${dMin}m ${dS}s` : `${dS}s`;

                      let timeStr = '';
                      try {
                        const dt = new Date(call.start_time);
                        timeStr = dt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
                      } catch { timeStr = call.start_time; }

                      const isNew = call.first_call;
                      return (
                        <tr key={call.id || idx} className="border-b hover:bg-gray-50" data-testid={`call-row-${idx}`}>
                          <td className="py-2 px-3">
                            <div className="flex items-center gap-2">
                              {call.answered ? (
                                <PhoneCall className="w-4 h-4 text-green-500 flex-shrink-0" />
                              ) : call.voicemail ? (
                                <Voicemail className="w-4 h-4 text-yellow-500 flex-shrink-0" />
                              ) : (
                                <PhoneMissed className="w-4 h-4 text-red-500 flex-shrink-0" />
                              )}
                              <span className="font-medium text-gray-900 truncate max-w-[140px]">{call.caller_name || 'Unknown'}</span>
                            </div>
                          </td>
                          <td className="py-2 px-3 text-gray-600">{call.phone}</td>
                          <td className="py-2 px-3 text-gray-600 truncate max-w-[150px]">{call.source}</td>
                          <td className="py-2 px-3">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                              isNew ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                            }`}>
                              {isNew ? 'New Patient' : 'Existing'}
                            </span>
                          </td>
                          <td className="py-2 px-3 text-gray-600">{timeStr}</td>
                          <td className="py-2 px-3 text-right text-gray-600">{durStr}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          )}
        </Card>
      )}

      {/* Google Reviews & Indexing */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard
          title="Google Reviews"
          value={metrics?.totalReviews || 0}
          subtitle={`${metrics?.averageRating || 0} avg rating`}
          icon={Star}
          testId="reviews-card"
        />
        <StatCard
          title="Pages Indexed"
          value={metrics?.pagesIndexed || 0}
          subtitle={`of ${metrics?.totalSitePages} total`}
          icon={Globe}
          testId="indexing-card"
        />
        <StatCard
          title="Direction Requests"
          value={"-"}
          subtitle="Requires GBP API"
          icon={MapPin}
          testId="directions-card"
        />
        <StatCard
          title="Website Clicks"
          value={"-"}
          subtitle="Requires GBP API"
          icon={MousePointer}
          testId="website-clicks-card"
        />
      </div>

      {/* Keyword Rankings */}
      <Card data-testid="keyword-rankings">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="w-5 h-5 text-teal-600" />
            Keyword Rankings
          </CardTitle>
          <CardDescription>Track your position for key local search terms</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-4 font-medium text-gray-600">Keyword</th>
                  <th className="text-center py-3 px-4 font-medium text-gray-600">Position</th>
                  <th className="text-center py-3 px-4 font-medium text-gray-600">Change</th>
                  <th className="text-right py-3 px-4 font-medium text-gray-600">Search Volume</th>
                </tr>
              </thead>
              <tbody>
                {metrics?.keywordRankings?.map((kw, idx) => (
                  <tr key={idx} className="border-b hover:bg-gray-50" data-testid={`keyword-row-${idx}`}>
                    <td className="py-3 px-4">
                      <span className="font-medium text-gray-900">{kw.keyword}</span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full font-bold ${
                        kw.position <= 3 ? 'bg-green-100 text-green-700' :
                        kw.position <= 10 ? 'bg-yellow-100 text-yellow-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {kw.position}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      {kw.change !== 0 && (
                        <span className={`inline-flex items-center ${kw.change > 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {kw.change > 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                          {Math.abs(kw.change)}
                        </span>
                      )}
                      {kw.change === 0 && <span className="text-gray-400">&mdash;</span>}
                    </td>
                    <td className="py-3 px-4 text-right text-gray-600">
                      {kw.volume.toLocaleString()}/mo
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Map Pack Performance */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card data-testid="indexing-status">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="w-5 h-5 text-teal-600" />
              Google Indexing Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Total Site Pages</span>
                <span className="font-bold text-gray-900">{metrics?.totalSitePages}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Pages Submitted to Google</span>
                <span className="font-bold text-green-600">{metrics?.pagesIndexed}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Submitted Today</span>
                <span className="font-bold text-blue-600">{metrics?.pagesSubmittedToday}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3 mt-4">
                <div 
                  className="bg-teal-600 h-3 rounded-full transition-all"
                  style={{ width: `${Math.min((metrics?.pagesIndexed / metrics?.totalSitePages) * 100, 100)}%` }}
                />
              </div>
              <p className="text-sm text-gray-500 text-center">
                {((metrics?.pagesIndexed / metrics?.totalSitePages) * 100).toFixed(0)}% of pages submitted
              </p>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="map-pack-performance">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="w-5 h-5 text-teal-600" />
              Map Pack Performance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                <span className="text-gray-700">Keywords in Top 3</span>
                <span className="font-bold text-green-600 text-xl">
                  {metrics?.keywordRankings?.filter(k => k.position <= 3).length || 0}
                </span>
              </div>
              <div className="flex justify-between items-center p-3 bg-yellow-50 rounded-lg">
                <span className="text-gray-700">Keywords in Top 10</span>
                <span className="font-bold text-yellow-600 text-xl">
                  {metrics?.keywordRankings?.filter(k => k.position <= 10).length || 0}
                </span>
              </div>
              <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                <span className="text-gray-700">Avg. Position</span>
                <span className="font-bold text-blue-600 text-xl">
                  {(metrics?.keywordRankings?.reduce((sum, k) => sum + k.position, 0) / 
                    (metrics?.keywordRankings?.length || 1)).toFixed(1)}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Info Note */}
      <Card className="bg-blue-50 border-blue-200" data-testid="info-note">
        <CardContent className="pt-6">
          <h3 className="font-semibold text-blue-800 mb-2">About This Dashboard</h3>
          <ul className="text-sm text-blue-700 space-y-1">
            <li><strong>Call tracking</strong> powered by CallRail — shows real call data with source attribution</li>
            <li><strong>Direction requests & website clicks</strong> will show real data once GBP API quota is approved</li>
            <li><strong>Keyword rankings</strong> are estimates — connect to a rank tracking tool for live data</li>
            <li><strong>ROI calculation</strong> uses your settings + actual first-time caller count from CallRail</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default ROIDashboard;
